import { api } from '../api/client'
import type { components } from '../api/generated/schema'

export type Profile = components['schemas']['UserResponse']

/**
 * Distinguishable from an auth failure: the credential was fine, our backend
 * was not.
 *
 * The cause travels on `Error`'s own `cause` rather than the plan's `cause_`
 * field — a `readonly` constructor parameter is TS1294 under this project's
 * `erasableSyntaxOnly`, and the standard option needs no field at all. It is
 * load-bearing: `sessionUnavailable` renders one screen for a 503 and for a
 * rejected CORS preflight, so the cause is the only thing that separates them
 * in a log afterwards.
 */
export class SessionError extends Error {
  constructor(cause: unknown) {
    super('session-unavailable', { cause })
    this.name = 'SessionError'
  }
}

/**
 * `X-Platform` goes through `headers` rather than typed params. `X-Device-Id`
 * is no longer set here: the client middleware (`api/deviceMiddleware.ts`)
 * attaches it to every request, this one included.
 *
 * It is absent from the generated operation because FastAPI never declared
 * it — `start_session` reads it with a raw `request.headers.get()`, so it
 * exists in the docstring and not in the schema. openapi-fetch passes `headers`
 * through untyped, which is the correct escape hatch for a header the spec does
 * not model.
 *
 * `x-user-email` / `x-user-name` ARE typed here and are deliberately not sent —
 * see `session.test.ts` for why, and for the guard.
 */
function sessionHeaders(): Record<string, string> {
  return { 'X-Platform': 'web' }
}

export async function startSession(): Promise<Profile> {
  try {
    const { data, error } = await api.POST('/api/v1/users/me/session', {
      headers: sessionHeaders(),
    })
    if (error !== undefined || data === undefined) throw new SessionError(error)
    return data
  } catch (cause) {
    // A rejected CORS preflight throws a TypeError from fetch rather than
    // returning a status, so `error` is never populated and the raw exception
    // would escape. Given CORS is this module's live blocker, that path is the
    // expected one rather than the exotic one.
    throw cause instanceof SessionError ? cause : new SessionError(cause)
  }
}

/**
 * The query function behind `keys.user.me()` — Settings > Preferences reads
 * `learn_from_edits` through it.
 *
 * `AuthState.signedIn.profile` holds the same record from `startSession`, so
 * `/users/me` is held twice and fetched twice on every signed-in load: once by
 * bootstrap, once by this query (the top-bar avatar always mounts a reader,
 * and Preferences and Profile read the same entry). That is deliberate for now:
 * `AuthProvider` mounts INSIDE `QueryProvider` and clears the whole cache on
 * every sign-out, so seeding from `bootstrap` is possible, but it would also
 * need every auth test to supply a client and a decision about a seed racing
 * that clear. Seeding is the follow-up that removes the second fetch. Meanwhile read mutable user fields
 * (`learn_from_edits`, `display_name`) from the `keys.user.me()` query, never
 * from `auth.profile`, which goes stale after a PATCH.
 */
export async function loadProfile(): Promise<Profile> {
  try {
    const { data, error } = await api.GET('/api/v1/users/me')
    if (error !== undefined || data === undefined) throw new SessionError(error)
    return data
  } catch (cause) {
    throw cause instanceof SessionError ? cause : new SessionError(cause)
  }
}
